/**
 * Report module translations - English
 * Includes: contract expiry alert, shop ledger, lease rate analysis, leasing progress, revenue monthly, collection alert
 */
export default {
  report: {
    // ==================== Contract Expiry Alert ====================
    contractExpiry: {
      title: 'Contract Expiry Alert',
      // Alert cards
      within30Days: 'Expiring in 30 Days',
      within90Days: 'Expiring in 90 Days',
      within180Days: 'Expiring in 180 Days',
      within1Year: 'Expiring in 1 Year',
      // Filters
      expiryRange: 'Expiry Range',
      building: 'Building',
      businessType: 'Business Type',
      days30: 'Within 30 days',
      days90: 'Within 90 days',
      days180: 'Within 180 days',
      days365: 'Within 1 year',
      // Business type options
      bizRetail: 'Retail',
      bizFood: 'Food & Beverage',
      bizEntertainment: 'Entertainment',
      bizService: 'Service',
      // Table columns
      contractNo: 'Contract No.',
      merchantName: 'Merchant',
      brandName: 'Brand',
      shopName: 'Shop',
      floorName: 'Floor',
      leaseArea: 'Lease Area (sqm)',
      monthlyRent: 'Monthly Rent',
      contractStart: 'Start Date',
      contractEnd: 'End Date',
      remainingDays: 'Days Left',
      contractStatus: 'Status',
      dayUnit: ' days',
    },
    // ==================== Collection Alert ====================
    collection: {
      totalReceivable: 'Total Receivable',
      totalReceived: 'Total Received',
      overdueAmount: 'Overdue Amount',
      overdueBillCount: 'Overdue Bills',
      avgOverdueDays: 'Avg Overdue Days',
      overdueDaysDistribution: 'Overdue Days Distribution',
      overdueAmountRatio: 'Overdue Amount Ratio',
      normalAmount: 'Normal Amount',
      billCount: 'Bill Count',
      unitBill: ' bills',
      days1to30: '1-30 Days',
      days31to60: '31-60 Days',
      days61to90: '61-90 Days',
      daysOver90: 'Over 90 Days',
      descTitle: 'Notes',
      desc1: 'Overdue amount is the total of unpaid bills past their due date. Data is updated daily.',
      desc2: 'Overdue days are calculated from the day after the bill due date. Average overdue days is the weighted average of all overdue bills.',
      desc3: 'It is recommended to focus on merchants overdue for more than 90 days and promptly initiate collection or legal procedures.',
    },

    // ==================== Revenue Monthly ====================
    revenue: {
      // KPI cards
      receivableTotal: 'Total Receivable',
      receivedAmount: 'Amount Received',
      arrearsAmount: 'Arrears Amount',
      collectionRate: 'Collection Rate',
      // Chart titles
      trendTitle: 'Monthly Revenue Trend (Last 12 Months)',
      compositionTitle: 'Fee Item Composition',
      floorCompareTitle: 'Floor Revenue Comparison',
      // Legend
      receivable: 'Receivable',
      received: 'Received',
      // Pie chart categories
      fixedRent: 'Fixed Rent',
      propertyFee: 'Property Fee',
      promotionFund: 'Promotion Fund',
      otherFee: 'Other',
      // Units
      tenThousand: '0k',
    },

    // ==================== Lease Rate Analysis ====================
    leaseRate: {
      title: 'Lease Rate Analysis',
      totalLeasableArea: 'Total Leasable Area',
      leasedArea: 'Leased Area',
      vacantArea: 'Vacant Area',
      overallLeaseRate: 'Overall Lease Rate',
      floorLeaseRate: 'Lease Rate by Floor',
      floorLeaseRateChart: 'Lease Rate by Floor',
      bizLeaseRate: 'Lease Rate by Business Type',
      businessTypeLeaseRateChart: 'Lease Rate by Business Type',
      detailTable: 'Detail Table',
      targetLine: 'Target',
      footerTotal: 'Total',
      colName: 'Name',
      totalShops: 'Total Shops',
      leasedShops: 'Leased Shops',
      colTotalShops: 'Total Shops',
      colRentedShops: 'Leased Shops',
      colTotalArea: 'Total Area (sqm)',
      colLeasedArea: 'Leased Area (sqm)',
      colRentedArea: 'Leased Area (sqm)',
      colVacantArea: 'Vacant Area (sqm)',
      colLeaseRate: 'Lease Rate (%)',
    },

    // ==================== Leasing Progress ====================
    leasing: {
      title: 'Leasing Progress Overview',
      totalShops: 'Total Shops',
      signRate: 'Signing Rate',
      openRate: 'Opening Rate',
      activeIntentions: 'Active Intentions',
      progressDistribution: 'Progress Distribution',
      floorSignRate: 'Signing Rate by Floor',
      bizSignStats: 'Signing by Business Type',
      signed: 'Signed',
      unsigned: 'Unsigned',
      statusEntered: 'Entered',
      statusRenovating: 'Renovating',
      statusSigned: 'Signed',
      statusActive: 'With Intention',
      statusNegotiating: 'Negotiating',
      statusVacant: 'Vacant',
      statusReserved: 'Reserved',
      statusMaintenance: 'Maintenance',
    },

    // ==================== Shop Ledger ====================
    shopLedger: {
      title: 'Shop Ledger Overview',
      // Stat cards
      totalShops: 'Total Shops',
      totalGLA: 'Total GLA',
      rentedArea: 'Rented Area',
      vacantArea: 'Vacant Area',
      leaseRate: 'Lease Rate',
      avgRentPrice: 'Avg. Rent Price',
      // Filters
      building: 'Building',
      floor: 'Floor',
      businessType: 'Business Type',
      shopStatus: 'Asset Status',
      keyword: 'Keyword',
      keywordPlaceholder: 'Asset code/name/merchant/brand',
      colBuilding: 'Building',
      colFloor: 'Floor',
      colShopCode: 'Asset Code',
      colShopName: 'Asset Name',
      colArea: 'Area',
      colRentableArea: 'Rentable Area',
      colBusinessType: 'Business Type',
      colShopStatus: 'Asset Status',
      colMerchant: 'Merchant',
      colBrand: 'Brand',
      colContractNo: 'Contract No.',
      colContractPeriod: 'Contract Period',
      colMonthlyRent: 'Monthly Rent',
      colLeasingStatus: 'Leasing Status',
      // Asset status labels
      statusRented: 'Rented',
      statusAvailable: 'Available',
      statusReserved: 'Reserved',
      statusMaintenance: 'Maintenance',
    },

    // ==================== Conversion Funnel ====================
    conversionFunnel: {
      title: 'Intention Conversion Funnel',
      // Funnel metrics
      negotiationCount: 'Negotiation Records',
      intentionTotal: 'Total Intentions',
      activeIntentionCount: 'Active Intentions',
      convertedCount: 'Converted to Contract',
      activeContractCount: 'Active Contracts',
      overallConversionRate: 'Overall Conversion Rate',
      intentionLossRate: 'Intention Loss Rate',
      depositRate: 'Deposit Collection Rate',
      // Funnel stages
      stageNegotiation: 'Negotiations',
      stageIntentionTotal: 'Total Intentions',
      stageActiveIntention: 'Active Intentions',
      stageConverted: 'Converted',
      stageActiveContract: 'Active Contracts',
      // Conversion rate
      conversionRate: 'Conversion Rate',
      funnelChart: 'Conversion Funnel',
      conversionChart: 'Stage Conversion Rates',
      // Description
      desc1: 'Overall conversion rate = Signed contracts / Total intentions × 100%',
      desc2: 'Intention loss rate = (Cancelled + Expired) / Total intentions × 100%',
    },

    // ==================== Brand Structure ====================
    brandStructure: {
      title: 'Brand & Business Structure',
      // KPI
      totalContracts: 'Total Contracts',
      totalArea: 'Total Area',
      totalShops: 'Total Shops',
      // Charts
      bizAreaDistribution: 'Area by Business Type',
      brandGradeDistribution: 'Brand Grade Distribution',
      brandTypeDistribution: 'Brand Type Distribution',
      // Grades
      gradeInternational: 'International',
      gradeNational: 'National',
      gradeRegular: 'Regular',
      // Types
      typeAnchor: 'Anchor Store',
      typeSubAnchor: 'Sub-Anchor',
      typeStandard: 'Standard',
      // Table columns
      colBusinessType: 'Business Type',
      colContractCount: 'Contracts',
      colArea: 'Area (sqm)',
      colAreaRatio: 'Area Ratio',
      colBrandCount: 'Brand Count',
    },

    // ==================== Business Planning & Brand Placement ====================
    planningAnalysis: {
      title: 'Planning & Placement Analysis',
      // KPI
      totalShops: 'Total Shops',
      completionRate: 'Completion Rate',
      coverageRate: 'Biz Type Coverage',
      primaryPlacementRate: 'Primary Brand %',
      avgDeviation: 'Avg Deviation',
      placedCount: 'Placed Brands',
      // Filters
      year: 'Plan Year',
      allYears: 'All Years',
      // Charts
      comparisonChart: 'Planned vs Actual by Floor',
      stackedChart: 'Floor Biz Distribution',
      placementPie: 'Placement Status',
      brandLevelChart: 'Brand Level Distribution',
      planned: 'Planned',
      actual: 'Actual',
      // Table columns
      colFloor: 'Floor',
      colBusinessType: 'Business Type',
      colTargetCount: 'Planned Shops',
      colActualCount: 'Actual Shops',
      colCompletionRate: 'Completion Rate',
      colTargetArea: 'Planned Area (sqm)',
      colActualArea: 'Actual Area (sqm)',
      colAreaAchieveRate: 'Area Achievement',
      colDeviation: 'Deviation (%)',
      colStatus: 'Status',
      // Status labels
      statusOnTrack: 'On Track',
      statusOffTrack: 'Off Track',
      statusCritical: 'Critical',
      // Placement status
      placedFormal: 'Formal Placement',
      placedInformal: 'Informal Placement',
      converted: 'Converted',
      cancelled: 'Cancelled',
      // Brand levels
      levelInternational: 'International',
      levelNational: 'National',
      levelOrdinary: 'Regular',
    },

    // ==================== Sales Statistics ====================
    salesStatistics: {
      title: 'Sales Statistics',
      // KPI
      monthlySales: 'Monthly Sales',
      yearlySales: 'YTD Sales',
      momGrowth: 'MoM Growth',
      yoyGrowth: 'YoY Change',
      reportedShopCount: 'Reporting Shops',
      // Charts
      monthlyTrend: 'Monthly Sales Trend (Last 12 Months)',
      bizSalesDistribution: 'Sales by Business Type',
      topShopsTitle: 'Top 10 Shops by Sales',
      topBrandsTitle: 'Top 10 Brands by Sales',
      // Table columns
      colRank: 'Rank',
      colShopName: 'Shop',
      colMerchant: 'Merchant',
      colBrand: 'Brand',
      colFloor: 'Floor',
      colSalesAmount: 'Sales Amount',
      colGrowth: 'MoM',
    },

    // ==================== Opening Progress ====================
    openingProgress: {
      title: 'Opening Progress',
      // KPI
      totalCount: 'Total Projects',
      renovatingCount: 'Renovating',
      pendingAcceptanceCount: 'Pending Acceptance',
      pendingOpeningCount: 'Pending Opening',
      openedCount: 'Opened',
      openingRate: 'Opening Rate',
      // Charts
      statusDistribution: 'Status Distribution',
      // Table columns
      colShopName: 'Shop',
      colMerchant: 'Merchant',
      colBrand: 'Brand',
      colRenovationType: 'Renovation Type',
      colPlannedStart: 'Planned Start',
      colPlannedEnd: 'Planned End',
      colActualStart: 'Actual Start',
      colActualEnd: 'Actual End',
      colOpeningDate: 'Opening Date',
      colDeviation: 'Deviation (days)',
      colStatus: 'Status',
      // Status
      statusRenovating: 'Renovating',
      statusPendingAcceptance: 'Pending Acceptance',
      statusPendingOpening: 'Pending Opening',
      statusOpened: 'Opened',
      statusCompleted: 'Completed',
    },

    // ==================== Work Order Reports ====================
    workOrder: {
      title: 'Work Order Statistics',
      // KPI
      totalCount: 'Total Orders',
      pendingCount: 'Pending',
      inProgressCount: 'In Progress',
      completedCount: 'Completed',
      avgHandlingHours: 'Avg Handling (hrs)',
      onTimeRate: 'On-Time Rate',
      responseRate: 'Response Rate',
      // Distribution
      distributionTitle: 'Work Order Distribution',
      byPriority: 'By Priority',
      byOrderType: 'By Type',
      bySource: 'By Source',
      byBuilding: 'By Building',
      byStatus: 'By Status',
      // Efficiency
      efficiencyTitle: 'Work Order Efficiency',
      avgResponseMinutes: 'Avg Response (min)',
      avgHandlingHoursShort: 'Avg Handling',
      responseRateShort: 'Response Rate',
      completionRate: 'Completion Rate',
      overdueCount: 'Overdue Orders',
      overdueRate: 'Overdue Rate',
      dailyTrend: 'Daily Order Trend',
      colCreated: 'Created',
      colCompleted: 'Completed',
      // Detail
      orderNo: 'Order No',
      orderType: 'Order Type',
      priority: 'Priority',
      colStatus: 'Status',
      colBuilding: 'Building',
      source: 'Source',
      shopName: 'Shop',
      creatorName: 'Creator',
      assignedName: 'Handler',
      createTime: 'Created',
      completedTime: 'Completed',
      handlingHours: 'Handling (hrs)',
      // Status
      statusPending: 'Pending',
      statusAssigned: 'Assigned',
      statusInProgress: 'In Progress',
      statusCompleted: 'Completed',
      statusClosed: 'Closed',
      statusCancelled: 'Cancelled',
      // Priority
      priorityUrgent: 'Urgent',
      priorityHigh: 'High',
      priorityNormal: 'Normal',
      priorityLow: 'Low',
      // Source
      sourceInternal: 'Internal',
      sourceMerchant: 'Merchant',
      sourcePublic: 'Public',
    },

    // ==================== Advertising Reports ====================
    advertising: {
      title: 'Advertising Overview',
      // KPI
      totalCount: 'Total Spaces',
      availableCount: 'Available',
      rentedCount: 'Rented',
      maintenanceCount: 'Maintenance',
      reservedCount: 'Reserved',
      totalArea: 'Total Area (m²)',
      totalMonthlyRent: 'Total Monthly Rent',
      avgMonthlyRentPerArea: 'Avg Rent (¥/m²)',
      availableRate: 'Available Rate',
      rentedRate: 'Rented Rate',
      // Analysis
      analysisTitle: 'Advertising Analysis',
      byType: 'By Type',
      byDisplayType: 'By Display',
      byBuilding: 'By Building',
      byFloor: 'By Floor',
      topByRent: 'Top by Monthly Rent',
      // Status
      statusAvailable: 'Available',
      statusRented: 'Rented',
      statusMaintenance: 'Maintenance',
      statusReserved: 'Reserved',
      statusDistribution: 'Status Distribution',
      // Type
      typeIndoor: 'Indoor',
      typeOutdoor: 'Outdoor',
      typeElevator: 'Elevator',
      typeCommonArea: 'Common Area',
      // Display
      displayStatic: 'Static',
      displayDigital: 'Digital',
      displayLed: 'LED',
      // Columns
      colName: 'Name',
      colArea: 'Area (m²)',
      colMonthlyRent: 'Monthly Rent',
      colRatio: 'Ratio',
      colBuilding: 'Building',
      colFloor: 'Floor',
      colRank: 'Rank',
    },

    // ==================== KPI Assessment ====================
    kpi: {
      title: 'KPI Assessment',
      personalTitle: 'Personal KPI',
      rankingTitle: 'Team Ranking',
      overviewTitle: 'Management Overview',
      periodType: 'Period',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      year: 'Year',
      month: 'Month',
      quarter: 'Quarter',
      selectUser: 'Select User',
      allUsers: 'All',
      targetValue: 'Target',
      actualValue: 'Actual',
      achievementRate: 'Achievement',
      unit: 'Unit',
      status: 'Status',
      overallAchievement: 'Overall Achievement',
      rank: 'Rank',
      userName: 'Name',
      coreIndicator: 'Core Indicator',
      export: 'Export',
      noData: 'No Data',
      roleTabs: {
        overview: 'Overview',
        recruitment: 'Recruitment',
        operation: 'Operations',
        finance: 'Finance',
        property: 'Property'
      },
      statusGreen: 'On Track',
      statusWarning: 'Warning',
      statusRed: 'At Risk',
      overviewCard: {
        teamSize: 'Team Size',
        passed: 'Passed',
        bestPerformer: 'Best Performer',
        bestRate: 'Best Rate'
      }
    },

  // ==================== P0 New Reports ====================
  receivableDetail: {
    title: 'Receivable & Received Detail',
    yearMonth: 'Month',
    contractNo: 'Contract No.',
    manualContractNo: 'Manual No.',
    merchantName: 'Merchant',
    brandName: 'Brand',
    assetName: 'Asset',
    feeItemName: 'Fee Item',
    calcType: 'Calc Type',
    receivableAmount: 'Receivable',
    paidAmount: 'Paid',
    diffAmount: 'Diff',
    overdueDays: 'Overdue Days',
    feeStatus: 'Status',
    dueDate: 'Due Date',
    statusPaid: 'Paid',
    statusPending: 'Pending',
    statusOverdue: 'Overdue',
    contractStatus: 'Contract Status',
    feeStatusFilter: 'Fee Status',
    merchantBrandFilter: 'Merchant/Brand',
    merchantBrandPlaceholder: 'Merchant or brand name',
    contractFilter: 'Contract',
    contractPlaceholder: 'Contract No.',
    totalReceivable: 'Total Receivable',
    totalPaid: 'Total Paid',
    totalDiff: 'Total Diff'
  },
  contractLedger: {
    title: 'Contract Ledger',
    contractNo: 'Contract No.',
    manualContractNo: 'Manual No.',
    merchantName: 'Merchant',
    brandName: 'Brand',
    assetName: 'Asset',
    assetTypeName: 'Asset Type',
    floorName: 'Floor',
    buildingName: 'Building',
    businessTypeName: 'Business Type',
    leaseArea: 'Area(㎡)',
    standardRent: 'Monthly Rent',
    contractStart: 'Start Date',
    contractEnd: 'End Date',
    contractStatusName: 'Status',
    signCompanyName: 'Sign Company',
    keyword: 'Keyword',
    assetType: 'Asset Type',
    contractStatus: 'Contract Status'
  },
  vacantAssetDetail: {
    title: 'Vacant Asset Detail',
    assetTypeName: 'Asset Type',
    assetCode: 'Asset Code',
    assetName: 'Asset Name',
    floorName: 'Floor',
    buildingName: 'Building',
    area: 'Area(㎡)',
    statusName: 'Status',
    vacantDays: 'Vacant Days',
    businessTypeName: 'Planned Type'
  },
  agingAnalysis: {
    title: 'Aging Analysis',
    contractNo: 'Contract No.',
    merchantName: 'Merchant',
    totalPlan: 'Total Plan',
    totalPaid: 'Total Paid',
    totalOverdue: 'Total Overdue',
    notOverdue: 'Not Overdue',
    within30: '1-30 Days',
    within31to60: '31-60 Days',
    within61to90: '61-90 Days',
    over90: 'Over 90 Days'
  },
  paymentReconciliation: {
    title: 'Payment Reconciliation',
    yearMonth: 'Month',
    contractNo: 'Contract No.',
    merchantName: 'Merchant',
    feeItemName: 'Fee Item',
    totalReceivable: 'Receivable',
    totalReceived: 'Received',
    diffAmount: 'Diff',
    collectionRate: 'Rate(%)',
    sumReceivable: 'Sum Receivable',
    sumReceived: 'Sum Received',
    sumDiff: 'Sum Diff',
    total: 'Total'
  },
  renewalWarning: {
    title: 'Renewal Warning',
    contractNo: 'Contract No.',
    merchantName: 'Merchant',
    brandName: 'Brand',
    assetName: 'Asset',
    assetTypeName: 'Asset Type',
    leaseArea: 'Area(㎡)',
    monthlyRent: 'Monthly Rent',
    contractStart: 'Start Date',
    contractEnd: 'End Date',
    remainingDays: 'Remaining Days',
    warningLevel: 'Warning Level',
    levelUrgent: 'Urgent(≤30d)',
    levelWarning: 'Warning(≤60d)',
    levelCaution: 'Caution(≤90d)',
    levelNormal: 'Normal'
  },
  negotiationDetail: {
    title: 'Negotiation Detail',
    negotiationDate: 'Date',
    merchantName: 'Merchant',
    brandName: 'Brand',
    assetName: 'Target Asset',
    negotiationTypeName: 'Type',
    negotiator: 'Negotiator',
    content: 'Content',
    resultName: 'Result',
    probabilityName: 'Probability',
    statusName: 'Status',
    followUpDate: 'Follow-up Date',
    nextSteps: 'Next Steps',
    statusFilter: 'Status',
    resultFilter: 'Result',
    keyword: 'Keyword'
  },
  salesSummary: {
    title: 'Sales Summary',
    yearMonth: 'Month',
    merchantName: 'Merchant',
    brandName: 'Brand',
    shopName: 'Shop',
    salesAmount: 'Sales',
    totalQuantity: 'Qty',
    efficiency: 'Efficiency',
    prevYearSales: 'Last Year',
    yoyRate: 'YoY(%)'
  },
  workOrderDetail: {
    title: 'Work Order Detail',
    orderNo: 'Order No.',
    title_col: 'Title',
    orderTypeName: 'Type',
    priorityName: 'Priority',
    statusName: 'Status',
    sourceName: 'Source',
    buildingName: 'Building',
    floorName: 'Floor',
    shopName: 'Location',
    creatorName: 'Creator',
    assignedName: 'Assignee',
    createTime: 'Created',
    acceptedTime: 'Accepted',
    completedTime: 'Completed',
    handlingHours: 'Hours',
    description: 'Description',
    orderType: 'Order Type',
    status: 'Status'
  },
  depositLedger: {
    title: 'Deposit Ledger',
    depositNo: 'Deposit No.',
    contractNo: 'Contract No.',
    merchantName: 'Merchant',
    depositTypeName: 'Type',
    feeItemName: 'Fee Item',
    totalAmount: 'Total',
    deductedAmount: 'Deducted',
    refundedAmount: 'Refunded',
    balance: 'Balance',
    statusName: 'Status',
    receiveDate: 'Receive Date',
    receiveMethodName: 'Method',
    receiveBy: 'Receiver',
    keyword: 'Keyword',
    depositType: 'Type',
    status: 'Status'
  },
  merchantLedger: {
    title: 'Merchant Ledger',
    keyword: 'Keyword',
    accountType: 'Account',
    bizType: 'Biz Type',
    businessMode: 'Mode',
    partnerRole: 'Partner Role',
    bizDate: 'Biz Date',
    entryNo: 'Entry No.',
    merchantName: 'Partner',
    contractNo: 'Contract No.',
    feeItemName: 'Fee Item',
    summary: 'Summary',
    bizNo: 'Biz No.',
    refDoc: 'Source Doc',
    debit: 'Debit',
    credit: 'Credit',
    balance: 'Balance',
    remark: 'Remark'
  },
  intentionConversion: {
    title: 'Intention Conversion',
    intentionNo: 'Intention No.',
    merchantName: 'Merchant',
    brandName: 'Brand',
    assetName: 'Target Asset',
    assetTypeName: 'Asset Type',
    intentionDate: 'Date',
    proposedRent: 'Proposed Rent',
    proposedArea: 'Proposed Area',
    depositAmount: 'Deposit',
    intentionStatusName: 'Status',
    conversionStatus: 'Conversion',
    contractNo: 'Contract No.',
    expiryDate: 'Expiry',
    creator: 'Creator',
    intentionStatus: 'Status',
    keyword: 'Keyword'
  },
  rentSalesRatio: {
    title: 'Rent-Sales Ratio',
    yearMonth: 'Month',
    merchantName: 'Merchant',
    brandName: 'Brand',
    shopName: 'Shop',
    salesAmount: 'Sales',
    rentAmount: 'Rent',
    rentSalesRatio: 'Ratio(%)',
    warningLevel: 'Warning'
  },
  energyAllocation: {
    title: 'Energy Allocation',
    yearMonth: 'Month',
    meterTypeName: 'Type',
    meterName: 'Meter',
    meterCode: 'Code',
    merchantName: 'Merchant',
    shopName: 'Shop',
    previousReading: 'Prev Reading',
    currentReading: 'Curr Reading',
    usageAmount: 'Usage',
    unitPrice: 'Unit Price',
    feeAmount: 'Fee',
    unit: 'Unit',
    readingDate: 'Date',
    reader: 'Reader',
    meterType: 'Type'
  },
  overdueDetail: {
    title: 'Overdue Detail',
    contractNo: 'Contract No.',
    merchantName: 'Merchant',
    brandName: 'Brand',
    assetName: 'Asset',
    yearMonth: 'Month',
    planAmount: 'Planned',
    paidAmount: 'Paid',
    overdueAmount: 'Overdue',
    overdueDays: 'Days',
    overdueRange: 'Range',
    dueDate: 'Due Date',
    planStatusName: 'Status',
    overdueRangeFilter: 'Overdue Range'
  }
  }
}
